import React, { MouseEventHandler } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import classnames from 'classnames';

import { ImageSize } from '@nxte-nl/image-hooks';
import { stateHooks } from '@nxte-nl/state-hooks';

import { ProductCardDataLayerContainer } from
  '../../containers/ProductCardDataLayerContainer/ProductCardDataLayerContainer';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { ITEM_POSITION_SHIFT } from '../../hooks/productListHooks/productListHooks';
import { ProductType } from '../../types/apiEnums';
import { ColorPresentational, ImagePresentational, ProductCardType } from '../../types/common';
import { ItemListID } from '../../types/dataLayer';
import { Button, ButtonFit, ButtonIntent } from '../Button/Button';
import { ImageWithSize } from '../ImageWithSize/ImageWithSize';
import { LinkIntent, Link, LinkType } from '../Link/Link';
import { Title } from '../Title/Title';
import { Typography, TypographyColor } from '../Typography/Typography';
import styles from './HeaderMenuGiftCardsTabs.module.css';

const messages = defineMessages({
  giftCards: {
    id: 'src.components.HeaderMenuGiftCardsTabs.giftCards',
    defaultMessage: 'Gift cards',
  },
  viewAllCategories: {
    id: 'src.components.HeaderMenuGiftCardsTabs.viewAllCategories',
    defaultMessage: 'View all categories',
  },
  viewAllCoupons: {
    id: 'src.components.HeaderMenuGiftCardsTabs.viewAllCoupons',
    defaultMessage: 'View all gift vouchers in this category',
  },
});

export const COUNT_OF_ITEMS_TO_SHOW_ON_MOBILE = 6;

export const hooks = {
  useHandleActiveTab(items: GiftcardTabItem[]) {
    const [activeItem, setActiveId] = React.useState<GiftcardTabItem>(items[0]);

    const handleSelectTab = React.useCallback((item: GiftcardTabItem) => {
      setActiveId(item);
    }, [setActiveId]);

    return {
      activeItem,
      handleSelectTab,
    };
  },

  useWrapHandleHoverTab(
    item: GiftcardTabItem,
    onHover: (item: GiftcardTabItem) => void,
    isDesktop: boolean,
  ) {
    return React.useCallback<MouseEventHandler>(() => {
      if (isDesktop) {
        return onHover(item);
      }
      return undefined;
    }, [isDesktop, item, onHover]);
  },

  useWrapHandleClickTab(
    item: GiftcardTabItem,
  ) {
    const redirect = navigationHooks.useRedirectCallback(
      React.useCallback(() => item.path || '', [item.path]),
    );
    return React.useCallback<MouseEventHandler>(() => {
      if (item.path) {
        return redirect();
      }
      return undefined;
    }, [item, redirect]);
  },

  useMenuItems(items: GiftcardTabItem[], isDesktop: boolean) {
    const { value: isAllMenuItemsShown, toggle } =
      stateHooks.useBooleanState({ initialState: false });
    const menuItems = React.useMemo(() => {
      if (isDesktop || isAllMenuItemsShown) {
        return items;
      }
      return items.slice(0, COUNT_OF_ITEMS_TO_SHOW_ON_MOBILE);
    }, [isDesktop, items, isAllMenuItemsShown]);

    const isShowMenuItemButtonShown = React.useMemo(
      () => items.length > menuItems.length,
      [items.length, menuItems.length],
    );

    const handleToggle = React.useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
      toggle();
    }, [toggle]);

    return {
      menuItems,
      toggleMenuItems: handleToggle,
      isShowMenuItemButtonShown,
    };
  },
};

export type Props = {
  isDesktop: boolean;
  items: GiftcardTabItem[];
};

export type TabItemProps = {
  activeId: string;
  handleHover(item: GiftcardTabItem): void;
  isDesktop: boolean;
  item: GiftcardTabItem;
};

export type GiftCardTopProduct = {
  backgroundColor: ColorPresentational;
  id: string;
  image: ImagePresentational;
  path: string | null;
  productType?: ProductType;
  showShadow: boolean;
  title: string;
};

export type GiftcardTabItem = {
  icon: ImagePresentational;
  id: string;
  path: string | null;
  title: string;
  topList: GiftCardTopProduct[];
};

export const HeaderMenuGiftCardsTabsItem = React.memo((props: TabItemProps) => {
  const { item, activeId, isDesktop, handleHover } = props;
  const { id, title, icon } = item;

  const handleHoverTab = hooks.useWrapHandleHoverTab(
    item,
    handleHover,
    isDesktop,
  );

  const handleClickTab = hooks.useWrapHandleClickTab(item);

  const isActive = id === activeId && isDesktop;

  return (
    <div
      onMouseOver={handleHoverTab}
      onClick={handleClickTab}
      className={classnames(styles.menuItem, {
        [styles.active]: isActive,
      })}>
      <ImageWithSize
        image={icon}
        size={ImageSize.M}
        className={styles.menuItemIcon}
      />

      <Typography className={styles.menuItemTitle}>{title}</Typography>
    </div>
  );
});

export const HeaderMenuGiftCardsTabs = React.memo((props: Props) => {
  const { isDesktop, items } = props;
  const { activeItem, handleSelectTab } = hooks.useHandleActiveTab(items);

  const { menuItems, isShowMenuItemButtonShown, toggleMenuItems } =
    hooks.useMenuItems(items, isDesktop);

  return (
    <div className={styles.container}>
      <div className={styles.itemsContainer}>
        {!isDesktop && <Title title={<FormattedMessage {...messages.giftCards} />} />}

        {menuItems.map(item => (
          <HeaderMenuGiftCardsTabsItem
            key={item.id}
            item={item}
            activeId={activeItem.id}
            handleHover={handleSelectTab}
            isDesktop={isDesktop}
          />
        ))}

        {!isDesktop && isShowMenuItemButtonShown && (
          <Button
            className={styles.viewAllCategoriesBtn}
            intent={ButtonIntent.linkish}
            fit={ButtonFit.Fill}
            onClick={toggleMenuItems}
          >
            <FormattedMessage {...messages.viewAllCategories} />
          </Button>
        )}
      </div>

      {isDesktop && <div className={styles.contentContainer}>

        <div className={styles.contentWrapper}>
          {
            activeItem.path ? (
              <div className={styles.viewAllCouponsContainer}>
                <Link
                  type={LinkType.Href} to={activeItem.path} intent={LinkIntent.primary}>
                  <Typography variant={'body1'} color={TypographyColor.LinkPrimary}>
                    <FormattedMessage {...messages.viewAllCoupons} />
                  </Typography>
                </Link>
              </div>
            ) : null
          }
          {activeItem.topList.map((card, index) => (
            <div key={index} className={styles.contentItem}>
              <ProductCardDataLayerContainer
                id={card.id}
                position={index + ITEM_POSITION_SHIFT}
                showHand={false}
                backgroundColor={card.backgroundColor}
                cardImage={card.image}
                title={card.title}
                path={card.path}
                backgroundImage={null}
                cardType={ProductCardType.S}
                productType={card.productType}
                itemListID={ItemListID.HEADER_MENU}
                showShadow={card.showShadow}
              />
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
});

HeaderMenuGiftCardsTabs.displayName = 'HeaderMenuGiftCardsTabs';
